import React from 'react'
import styled from 'styled-components'
import Typography from 'ui-components/lib/components/Typography'
import { useTheme } from '../../contexts/ThemeContext'

const CardContainer = styled.div`
  position: relative;
  background: ${props => props.bgColor || 'var(--card-bg)'};
  border-radius: 20px;
  padding: 1.75rem;
  min-height: 160px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 0 2px 4px rgba(0, 0, 0, 0.04);
  border: 1px solid ${props => props.borderColor || 'transparent'};
  overflow: hidden;
  backdrop-filter: blur(10px);

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 5px;
    background: linear-gradient(90deg, ${props => props.accentColor || props.bgColor} 0%, ${props => props.accentColor || props.bgColor}dd 100%);
    opacity: 1;
  }

  &::after {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 200%;
    height: 200%;
    background: radial-gradient(circle, rgba(255, 255, 255, 0.1) 0%, transparent 70%);
    opacity: 0;
    transition: opacity 0.3s ease;
  }

  &:hover {
    transform: translateY(-6px) scale(1.02);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1);
    
    &::after {
      opacity: 1;
    }
  }

  &:active {
    transform: translateY(-3px) scale(1.01);
  }

  .dark & {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4), 0 2px 4px rgba(0, 0, 0, 0.3);
    
    &:hover {
      box-shadow: 0 12px 32px rgba(0, 0, 0, 0.6), 0 4px 8px rgba(0, 0, 0, 0.4);
    }
  }
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1rem;
`

const CardTitle = styled(Typography)`
  font-size: 0.875rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: ${props => props.textColor || 'var(--text-secondary)'};
  margin: 0;
`

const CardIconWrapper = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${props => props.iconBg ? `${props.iconBg}20` : 'rgba(255, 255, 255, 0.2)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  flex-shrink: 0;
  transition: transform 0.3s ease, background-color 0.3s ease;
  
  svg {
    color: ${props => props.iconBg || 'inherit'};
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1));
  }

  ${CardContainer}:hover & {
    transform: scale(1.1) rotate(5deg);
    background: ${props => props.iconBg ? `${props.iconBg}30` : 'rgba(255, 255, 255, 0.3)'};
  }
`

const CardContent = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
`

const CardCount = styled(Typography)`
  font-size: 3rem;
  font-weight: 800;
  line-height: 1;
  color: ${props => props.textColor || 'var(--text-color)'};
  margin: 0;
  font-family: 'Inter', sans-serif;
  letter-spacing: -0.02em;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease;

  ${CardContainer}:hover & {
    transform: scale(1.05);
  }
`

const CardSubtitle = styled(Typography)`
  font-size: 0.75rem;
  color: ${props => props.textColor || 'var(--text-secondary)'};
  margin: 0;
  opacity: 0.8;
`

const ModernCard = ({ 
  title, 
  count, 
  subtitle,
  bgColor, 
  textColor, 
  accentColor,
  icon,
  onClick,
  loading,
  children 
}) => {
  const { isDark } = useTheme()

  // Ajustar cores para tema escuro se necessário
  const getTextColor = () => {
    if (textColor) return textColor
    // Se o card tem cor de fundo clara, usar texto escuro
    if (bgColor && !bgColor.includes('hsl') && bgColor !== '#FFFFFF') {
      return '#4E4B59'
    }
    return isDark ? 'hsl(210, 20%, 95%)' : '#4E4B59'
  }

  const getBorderColor = () => {
    if (isDark) {
      return 'hsl(215, 25%, 22%)'
    }
    return 'rgba(0, 0, 0, 0.05)'
  }

  return (
    <CardContainer
      bgColor={bgColor}
      borderColor={getBorderColor()}
      accentColor={accentColor || bgColor}
      onClick={onClick}
    >
      <CardHeader>
        <CardTitle textColor={getTextColor()}>
          {title}
        </CardTitle>
        {icon && (
          <CardIconWrapper iconBg={accentColor || bgColor}>
            {icon}
          </CardIconWrapper>
        )}
      </CardHeader>
      <CardContent>
        <div>
          <CardCount textColor={getTextColor()}>
            {loading ? '...' : count}
          </CardCount>
          {subtitle && (
            <CardSubtitle textColor={getTextColor()}>
              {subtitle}
            </CardSubtitle>
          )}
        </div>
        {children}
      </CardContent>
    </CardContainer>
  )
}

export default ModernCard
